/* eslint-disable import/no-unresolved */
import CustomTextField from "src/shared/CustomTextField";
import CustomSelect from "src/shared/CustomSelect";
import CustomCheckBox from "src/shared/CustomCheckBox";
import { useCallback, useEffect, useMemo } from "react";
import { GENERAL_DETAILS_MAPPING, getAssignmentFlag } from "src/helpers/FormDetails/resources";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import {
  calcAgeFromToday,
  findKeysByValue
} from "src/helpers/FormDetails/ProposalDetails/resources";
import { getMasterPolicy } from "src/services/FormDetails/CreateLead";
import { useQuery } from "react-query";

interface Props {
  formik: any;
  genericDetails: any;
}

function BasicInfo({ formik, genericDetails }: Props) {
  const partnerName = useAppSelector((state) => state.common?.leadDetails?.partnerId);
  const insurerStateCode = useAppSelector((state) => state.common?.leadDetails?.lifeInsurerState);
  const isProportionateLead = useAppSelector((state) => state.common?.isProportionateLead);
  const productId = useAppSelector((state) => state.common.productId);
  const partnerID = useAppSelector((state) => state.common.leadDetails.partnerId);
  const coverOption = useAppSelector((state) => state.common.leadDetails.coverOption);
  const defaultStaffMember = useAppSelector((state) => state.common.staffMember);
  const lifeInsurerDob = useAppSelector((state) => state.proposerDetails?.general?.dob);
  const proposerAge = useAppSelector((state) => state.common?.leadDetails?.proposerAge);
  const lifeInsurerAge = lifeInsurerDob ? calcAgeFromToday(lifeInsurerDob!) : proposerAge;
  const loanCategory = useAppSelector((state) => state.loanDetails?.loan?.loanTypeCode || "");
  const assignmentFlagStatus = !!useAppSelector(
    (state) => state.common.partnerData?.showAssignmentFlag
  );
  const masterPolicyHolderName = useAppSelector(
    (state) => state.common.partnerData?.masterPolicyHolderName
  );
  const riderFlag = useAppSelector((state) => state?.loanDetails?.isRiderSelectionLocked);

  const showAssignmentFlag = useMemo(
    () => getAssignmentFlag(assignmentFlagStatus),
    [assignmentFlagStatus]
  );

  const st2CodeMapping = useCallback(
    (partnerId: string, stateCode: number): string => {
      if (partnerId === "idbi") {
        if (stateCode === 32) return "9997"; // code for state Kerala-32
        return "9998";
      }
      return "9999";
    },
    [formik.values?.isStaff]
  );

  // const dispatch = useDispatch();
  // const toggleRiderForm = useAppSelector((state) => state.loanDetails.showRiderForm);

  useEffect(() => {
    if (!formik.values?.isStaff) {
      formik.setFieldValue("staffEmpCode", "");
      formik.setFieldValue("staffST2Code", "");
      formik.setFieldValue("staffCompany", "");
    } else {
      formik.setFieldValue("staffST2Code", st2CodeMapping(partnerName, insurerStateCode));
    }
  }, [formik.values?.isStaff]);

  useEffect(() => {
    formik.setFieldValue(
      "staffCompanyCode",
      findKeysByValue(GENERAL_DETAILS_MAPPING?.companyName(partnerName), formik.values.staffCompany)
    );
  }, [formik.values.staffCompany]);

  useQuery(
    [
      "masterPolicy",
      productId,
      partnerID,
      formik.values.isStaff,
      lifeInsurerAge,
      loanCategory,
      coverOption
    ],
    () =>
      getMasterPolicy(
        productId,
        partnerID,
        formik.values.isStaff,
        lifeInsurerAge,
        loanCategory,
        coverOption
      ),
    {
      enabled: !!productId && !!partnerID,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        formik.setFieldValue("masterPolicyNumber", response?.data?.masterPolicyNumber || "");
      }
    }
  );

  useEffect(() => {
    formik.setFieldValue("masterPolicyHolderName", masterPolicyHolderName);
  }, [formik.values.isStaff]);

  useEffect(() => {
    if (defaultStaffMember) {
      formik.setFieldValue("isStaff", true);
    }
  }, [defaultStaffMember]);

  const isRiderActive = useAppSelector((state) => state.common.isRiderActive);

  return (
    <div>
      <div className="flex flex-col md:flex-row mb-6 justify-between">
        {genericDetails.applicationNo?.visible && (
          <div className="w-full mb-6 md:mb-0 md:w-222/1000">
            <CustomTextField
              requiredStar={genericDetails.applicationNo?.required}
              label={genericDetails.applicationNo?.label}
              className="222/1000"
              disabledField={genericDetails.applicationNo?.disabled}
              onChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              value={formik.values.applicationNumber}
              name="applicationNumber"
              error={formik.errors.applicationNumber}
              touched={formik.touched.applicationNumber}
            />
          </div>
        )}
        {genericDetails.bankBranchCode?.visible && (
          <div className="w-full mb-6 md:mb-0 md:w-222/1000">
            <CustomTextField
              requiredStar={genericDetails.bankBranchCode?.required}
              disabledField={genericDetails.bankBranchCode?.disabled || isProportionateLead}
              label={genericDetails.bankBranchCode?.label}
              className=""
              onChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              value={formik.values.serviceBranchCode}
              name="serviceBranchCode"
              error={formik.errors.serviceBranchCode}
              touched={formik.touched.serviceBranchCode}
            />
          </div>
        )}
        {genericDetails.bankBranchName?.visible && (
          <div className="w-full mb-6 md:mb-0 md:w-222/1000">
            <CustomTextField
              requiredStar={genericDetails.bankBranchName?.required}
              label={genericDetails.bankBranchName?.label}
              disabledField={genericDetails.bankBranchName?.disabled || isProportionateLead}
              className=""
              onChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              value={formik.values.serviceBranchName}
              name="serviceBranchName"
              error={formik.errors.serviceBranchName}
              touched={formik.touched.serviceBranchName}
            />
          </div>
        )}
        {genericDetails.bankDistributorName?.visible && (
          <div className="w-full md:w-222/1000">
            <CustomSelect
              requiredStar={genericDetails.bankDistributorName?.required}
              label={genericDetails.bankDistributorName?.label}
              className="222/1000"
              options={["IDBI", "FEDERAL", "JFS", "SREI", "OTHERS", "DIRECT", "CAPRI", "SOLFIN"]}
              disabledField={genericDetails.bankDistributorName?.disabled}
              onChange={formik.handleChange}
              setFieldTouched={formik.setFieldTouched}
              name="bankDistributorName"
              value={formik.values.bankDistributorName}
              error={formik.errors.bankDistributorName}
              touched={formik.touched.bankDistributorName}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
        {genericDetails.masterPolicyNumber?.visible &&
          genericDetails.masterPolicyHolderName?.visible && (
            <>
              <div>
                <CustomTextField
                  requiredStar={genericDetails.masterPolicyNumber?.required}
                  label={genericDetails.masterPolicyNumber?.label}
                  disabledField={genericDetails.masterPolicyNumber?.disabled}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  value={formik.values.masterPolicyNumber}
                  name="masterPolicyNumber"
                  error={formik.errors.masterPolicyNumber}
                  touched={formik.touched.masterPolicyNumber}
                />
              </div>
              <div>
                <CustomTextField
                  requiredStar={genericDetails.masterPolicyHolderName?.required}
                  label={genericDetails.masterPolicyHolderName?.label}
                  disabledField={genericDetails.masterPolicyHolderName?.disabled}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  value={formik.values.masterPolicyHolderName}
                  name="masterPolicyHolderName"
                  error={formik.errors.masterPolicyHolderName}
                  touched={formik.touched.masterPolicyHolderName}
                />
              </div>
            </>
          )}
        {(formik.values.ageasFederalEmployeeCode ||
          genericDetails.ageasFederalEmployeeCode?.visible) && (
          <div>
            <CustomTextField
              label={genericDetails.ageasFederalEmployeeCode?.label}
              disabledField={genericDetails.ageasFederalEmployeeCode?.disabled}
              onChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              value={formik.values.ageasFederalEmployeeCode}
              name="ageasFederalEmployeeCode"
              error={formik.errors.ageasFederalEmployeeCode}
              touched={formik.touched.ageasFederalEmployeeCode}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 sm:my-8 mt-4">
        {genericDetails.staffFlag?.visible && (
          <div>
            <CustomCheckBox
              label={genericDetails.staffFlag?.label}
              isReview={defaultStaffMember}
              name="isStaff"
              handleBlur={formik.handleBlur}
              setFieldValue={formik.setFieldValue}
              checked={formik.values?.isStaff || genericDetails.staffFlag?.defaultValue}
            />
          </div>
        )}
        {showAssignmentFlag && (
          <div>
            <CustomCheckBox
              label={genericDetails.assignmentClauseFlag?.label}
              name="assignmentFlag"
              handleBlur={formik.handleBlur}
              setFieldValue={formik.setFieldValue}
              checked={formik.values?.assignmentFlag}
            />
          </div>
        )}
        {isRiderActive && (
          <div>
            <CustomCheckBox
              label="Do you wish to opt for rider?"
              name="addRiders"
              handleBlur={formik.handleBlur}
              setFieldValue={formik.setFieldValue}
              checked={formik.values?.addRiders}
              disabled={!!riderFlag}
            />
          </div>
        )}
      </div>

      {formik.values?.isStaff && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6 mt-4">
          <CustomSelect
            requiredStar={genericDetails.staffCompany?.required}
            label={genericDetails.staffCompany?.label}
            options={Object.values(GENERAL_DETAILS_MAPPING.companyName(partnerName))}
            onChange={formik.handleChange}
            setFieldTouched={formik.setFieldTouched}
            name="staffCompany"
            value={formik.values.staffCompany}
            error={formik.errors.staffCompany}
            touched={formik.touched.staffCompany}
          />
          <CustomTextField
            requiredStar={genericDetails.employeeCode?.required}
            label={genericDetails.employeeCode?.label}
            onChange={formik.handleChange}
            handleBlur={formik.handleBlur}
            value={formik.values.staffEmpCode}
            name="staffEmpCode"
            error={formik.errors.staffEmpCode}
            touched={formik.touched.staffEmpCode}
          />
          <CustomTextField
            requiredStar={genericDetails.st2Code?.required}
            disabledField={genericDetails.st2Code?.disabled}
            label={genericDetails.st2Code?.label}
            onChange={formik.handleChange}
            handleBlur={formik.handleBlur}
            value={formik.values.staffST2Code}
            name="staffST2Code"
            error={formik.errors.staffST2Code}
            touched={formik.touched.staffST2Code}
          />
        </div>
      )}
    </div>
  );
}

export default BasicInfo;
